import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { useTabBarVisible } from "@/components/MobileTabBar";
import { Share, PlusSquare, X, Smartphone } from "lucide-react";

const KEY = "velluto_install_dismissed";
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent) || window.matchMedia?.("(max-width: 768px)").matches;

// One-time, gentle "add Velluto to your home screen" invitation for phone visitors.
export function InstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const tabBar = useTabBarVisible();
  const inThread = /^\/messages\/.+/.test(useLocation().pathname);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  useEffect(() => {
    if (!user || localStorage.getItem(KEY) || isStandalone() || !isMobile()) return;
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "accepted") { localStorage.setItem(KEY, "installed"); setShow(false); }
  };
  const ios = isIOS();

  return (
    <AnimatePresence>
      {show && !inThread && (
        <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 26 }}
          data-testid="install-prompt" className={`fixed inset-x-3 ${tabBar ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]" : "bottom-[max(0.75rem,env(safe-area-inset-bottom))]"} md:bottom-3 z-[60] sm:left-auto sm:right-4 sm:w-[380px]`}>
          <div className="rounded-3xl border border-gold/40 bg-surface/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-4 flex gap-3">
            <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-2xl border border-gold/30 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm"><span className="brand-script text-xl align-middle mr-1">Velluto</span> on your home screen</p>
              {ios ? (
                <p data-testid="install-ios-steps" className="text-xs text-text-muted mt-1 leading-relaxed">
                  Tap <Share size={12} className="inline -mt-0.5 text-gold" /> <span className="text-text-main">Share</span>, then <PlusSquare size={12} className="inline -mt-0.5 text-gold" /> <span className="text-text-main">Add to Home Screen</span>. Opens full-screen, no browser bar — discreet.
                </p>
              ) : (
                <p className="text-xs text-text-muted mt-1">One tap: full-screen, faster, and nothing in your browser history.</p>
              )}
              <div className="flex items-center gap-2 mt-3">
                {!ios && deferred && (
                  <button data-testid="install-btn" onClick={install} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.15em] hover:bg-gold/90"><Smartphone size={12} /> Add to home screen</button>
                )}
                <button data-testid="install-dismiss" onClick={dismiss} className="px-3 py-2 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.15em] hover:text-text-main">{ios || !deferred ? "Got it" : "Not now"}</button>
              </div>
            </div>
            <button data-testid="install-close" onClick={dismiss} aria-label="Dismiss" className="self-start p-1 text-text-muted hover:text-text-main"><X size={14} /></button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
